import './quantity-input.scss';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useOnClickOutside } from '../../../util/use-on-click-outside';

const DEFAULT_INCREMENT = 1;

export const QuantityInput = ({
  name,
  value,
  min,
  max,
  increment,
  onChange,
  disabled = false,
}: {
  name: string;
  value?: string;
  min?: number;
  max?: number;
  increment?: number;
  onChange?: (v: string | undefined) => void;
  disabled?: boolean;
}) => {
  const [error, setError] = useState<string | boolean>();
  const divRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const [valueNumber, setValueNumber] = useState(parseFloat(value as any));

  useEffect(() => {
    setValueNumber(parseFloat(value as any));
  }, [value]);

  useOnClickOutside(divRef, () => {
    setIsFocused(false);
  });

  useEffect(() => {
    if (value && min && valueNumber < min) {
      setError(`Min quantity: ${min}`);
    } else if (value && max && valueNumber > max) {
      setError(`Max quantity: ${max}`);
    } else {
      setError(false);
    }
  }, [value]);

  function handleFocus() {
    setIsFocused(true);
    inputRef.current?.focus();
  }

  function incrementQuantity() {
    if (onChange && (!max || (valueNumber || 0) < max)) {
      let newValue = (valueNumber || 0) + (increment || DEFAULT_INCREMENT);
      if (min && newValue < min) {
        newValue = min;
      }
      onChange(newValue.toString());
    }
  }

  function decrementQuantity() {
    if (onChange && (!min || (valueNumber || 0) > min)) {
      let newValue = (valueNumber || 0) - (increment || DEFAULT_INCREMENT);
      if (max && newValue > max) {
        newValue = max;
      }
      if (min && newValue < min) {
        newValue = min;
      }
      onChange(newValue.toString());
    }
  }

  return (
    <div className="quantity-wrapper">
      <div
        ref={divRef}
        className={classNames('input-wrapper', { focused: isFocused, invalid: !!error })}
        onClick={handleFocus}
      >
        <i
          className={classNames('quantity-button fa fa-minus', { disabled: min && value && valueNumber <= min })}
          onClick={decrementQuantity}
          data-cy="quantity-decrement"
        />
        <input
          className={classNames({ invalid: !!error })}
          ref={inputRef}
          type="text"
          disabled={disabled}
          name={name}
          value={value || ''}
          size={3}
          onChange={(v) => {
            const inputText = v.target.value;

            // Ignore empty text, since most likely user is still typing
            if (inputText === '') {
              onChange && onChange(inputText);
              return;
            }

            // Determine if increment is a number (not an integer)
            const isDecimalAllowed = typeof increment === 'number' && !Number.isInteger(increment);

            // Use regex to allow only valid integers, or numbers (if decimal increment) and negative numbers
            const regex = isDecimalAllowed ? /^-?\d*\.?\d*$/ : /^-?\d*$/;

            if (!regex.test(inputText)) {
              setError('Invalid quantity');
              return;
            }

            const newValue = isDecimalAllowed ? parseFloat(inputText) : parseInt(inputText);

            // Ensure manual input is valid
            if (newValue === 0) {
              setError('Invalid quantity');
            } else if (newValue && min && newValue < min) {
              setError(`Min quantity: ${min}`);
            } else if (newValue && max && newValue > max) {
              setError(`Max quantity: ${max}`);
            } else {
              setError(false);
              onChange && onChange(inputText);
            }
          }}
          data-cy="quantity-input"
        />
        <i
          className={classNames('quantity-button fa fa-plus', { disabled: max && value && valueNumber >= max })}
          onClick={incrementQuantity}
          data-cy="quantity-increment"
        />
      </div>
      <span className={classNames('error-message', { visible: error })}>{error}</span>
    </div>
  );
};
